/* eslint-disable max-lines */
import { captureException } from '@sentry/browser';
import { initAndLoadMyOffice } from 'Cloud/Application/Editor/MyOffice/myOffice.helpers';
import { MYOFFICE_VARIANTS } from 'Cloud/Application/Editor/MyOffice/myOffice.types';
import { initAndLoadR7 } from 'Cloud/Application/r7helpers';
import { getViewerFor } from 'Cloud/ui/Viewer/viewerFactory';
import classNames from 'clsx';
import { logger } from 'lib/logger';
import { xray } from 'lib/xray';
import React, { memo, ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { getDocsView, getDocumentUrl } from 'reactApp/api/axios.corsapi';
import { IS_MOBILE_BROWSER, IS_PUBLIC, IS_STOCK } from 'reactApp/appHelpers/configHelpers';
import { isTemporaryAttachView } from 'reactApp/appHelpers/featuresHelpers';
import { getStockIdOfFile } from 'reactApp/modules/attaches/attaches.helpers';
import { AttachesItem, EAttachTypes } from 'reactApp/modules/attaches/attaches.types';
import { EditorItem } from 'reactApp/modules/editor/editor.types';
import {
    getFeatureMyOfficeForMailAttaches,
    getFeatureMyOfficeForMailWopi,
    getFeatureMyOfficeForPublicAndStock,
    getFeatureMyOfficeSendDetailRadars,
    getMyOfficeProgressOn,
} from 'reactApp/modules/features/features.selectors';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { CloudFile, CloudItem, EStorageType } from 'reactApp/modules/storage/storage.types';
import { ViewerSelectors } from 'reactApp/modules/viewer/viewer.selectors';
import { RootState } from 'reactApp/store';
import { CONTENT_LOAD_TIMEOUT } from 'reactApp/ui/ReactViewer/ReactViewer.constants';
import { ATTACHES_DOCS_URL, getDocViewData } from 'reactApp/ui/ReactViewer/ReactViewer.helpers';
import { useIframeState } from 'reactApp/ui/ReactViewer/ViewerDoc/hooks/useIframeState';
import { ProgressBar } from 'reactApp/ui/ReactViewer/ViewerDoc/ProgressBar/ProgressBar';
import { Spinner } from 'reactApp/ui/Spinner/Spinner';
import { sendXray } from 'reactApp/utils/ga';
import { DOC_PLACEHOLDER_ID, getMyOfficeIframeId, noop, normalizeSymbols } from 'reactApp/utils/helpers';
import { callWopiEmbeddedViewAPICall, callWopiViewAPICall } from 'reactApp/utils/wopiHelpers';

import { shallowEqualMobile } from './helpers/shallowEqualMobile';
import styles from './ViewerDoc.css';

const rlog = 'cloud-viewer-doc';

interface Props {
    file: CloudFile;
    renderError: () => ReactElement;
    onReady?: (props?: { skipSendAnalytics?: boolean }) => void;
    onOpen?: () => void;
    onError?: () => void;
    embedded?: boolean;
    attachDocUrl?: string;
    isMobile?: boolean;
}

export const ViewerDoc = memo<Props>(
    // eslint-disable-next-line max-lines-per-function, sonarjs/cognitive-complexity, complexity
    ({
        file,
        renderError,
        onReady = noop,
        onError = noop,
        onOpen = noop,
        embedded = false,
        attachDocUrl = '',
        isMobile,
    }): ReactElement | null => {
        const [url, setUrl] = useState('');
        const [docState, setDocState] = useState({ isError: false, isLoaded: false });
        const [showMyOfficeProgressBar, setShowMyOfficeProgressBar] = useState(false);

        const isWopiEnabled = useSelector((state: RootState) => ViewerSelectors.isWopiEnabled(state, file.ext?.toLowerCase()));

        const storage = useSelector(getCurrentStorage);
        const isAttach = storage === EStorageType.attaches;
        const isEmbedded = storage === EStorageType.embedded;
        const viewerApi = useMemo(() => getViewerFor(file.ext, isAttach, storage, file), [file.ext, isAttach]);

        const isMyOfficeForMailWopiFeature = useSelector(getFeatureMyOfficeForMailWopi) && isWopiEnabled && viewerApi.isMyOffice;
        const isCloudStock = (file as AttachesItem).attachType === EAttachTypes.cloudStock;

        const isMyOfficeForMailWopi = isAttach && isMyOfficeForMailWopiFeature;

        const [docViewData, setDocViewData] = useState({
            url: null,
            post_params: {
                access_token: null,
                access_token_ttl: null,
            },
            post_content_type: null,
            isDocumentsViewWopiLoading: isMyOfficeForMailWopi,
        });

        const errorTimeout = useRef<number>();

        const iframeRef = useRef<HTMLIFrameElement>(null);
        const iframeWopiRef = useRef<HTMLIFrameElement>(null);
        const iframeAmrRef = useRef<HTMLIFrameElement>(null);

        const iframeDocsRef = useRef<HTMLIFrameElement>(null);

        const isMyOfficeForMail = useSelector(getFeatureMyOfficeForMailAttaches);
        const isMyOfficeForPublicAndStock = useSelector(getFeatureMyOfficeForPublicAndStock);
        const isMyOfficeProgressBar = useSelector(getMyOfficeProgressOn);
        const isMyOfficeSendDetailRadars = useSelector(getFeatureMyOfficeSendDetailRadars);

        const docId = DOC_PLACEHOLDER_ID + normalizeSymbols(file.name);

        const { activeIframe, setVisibilityListener } = useIframeState(setDocState, setDocViewData);

        // Отдельная фича для аттачей isMyOfficeForMailWopi - для остальных isWopiEnabled
        const wopiEnableCondition = isAttach ? isMyOfficeForMailWopi && isWopiEnabled : isWopiEnabled;
        const myOfficeVariant = wopiEnableCondition ? MYOFFICE_VARIANTS.wopi : MYOFFICE_VARIANTS.amr;

        const handleError = useCallback(() => {
            setDocState({
                isError: true,
                isLoaded: true,
            });
            onReady({ skipSendAnalytics: true });
            onError();
            xray.send('view-doc-error');
        }, [onError, onReady]);

        const initShowAmrIframe = useCallback(() => {
            if (iframeAmrRef.current) {
                iframeAmrRef.current.style.opacity = '1';
            }
        }, [iframeAmrRef]);

        const showMyOfficeIframe = useCallback(() => {
            setShowMyOfficeProgressBar(false);
            initShowAmrIframe();
        }, []);

        const setDocumentLoaded = () => {
            setDocState({
                isError: false,
                isLoaded: true,
            });

            onOpen();

            // Если включен прогресс-бар, он сам вызовет эту функцию с небольшой задержкой
            if (!isMyOfficeProgressBar && viewerApi.isMyOffice) {
                showMyOfficeIframe();
            }
        };

        const onDocumentReady = useCallback(() => {
            if (errorTimeout.current) {
                clearTimeout(errorTimeout.current);
            }
            setDocumentLoaded();

            if (!isWopiEnabled && viewerApi.isMyOffice) {
                setVisibilityListener();
            }

            if (isMyOfficeSendDetailRadars && (viewerApi.isMyOffice || viewerApi.wopi)) {
                /** Проверка на флаг для моего офиса(моб/десктоп)
                 *
                 *  {@link https://jira.vk.team/browse/CLOUDWEB-13905 Задача}
                 */
                const deviceType = !isMobile ? 'desktop' : 'mobile';
                const viewerType = isWopiEnabled && viewerApi.wopi ? 'wopi' : 'amr';
                const mode = 'view';
                const ext = file.ext;

                /** Формат радара для моего офиса
                 *    @example Пример.
                 *    myoffice_open_<device_type>_<viewer_type>_<mode>_<ext>
                 */
                xray.send(`myoffice_open_${deviceType}_${viewerType}_${mode}_${ext}`);
            }
        }, []);

        const handlePostMessage = useCallback(
            (event) => {
                /**
                 * На window может быть навешено несколько обработчиков
                 * TODO: так надо сделать потом для всех типов viewerApi
                 */
                if (viewerApi.isDocsMail && iframeRef?.current?.contentWindow !== event.source) {
                    return;
                }

                // TODO: это для МойОфис? Почему условие только isAttach
                if (isAttach && !viewerApi.isDocsMail) {
                    const { data } = event;

                    if (typeof data !== 'object') {
                        return;
                    }

                    const { type, docViewData } = data;

                    if (type === 'documentsViewUrl') {
                        setDocViewData({
                            isDocumentsViewWopiLoading: false,
                            ...docViewData,
                        });
                        setDocumentLoaded();
                        clearTimeout(errorTimeout.current);
                        xray.send('view-doc-get-url');
                    }

                    if (type === 'ready') {
                        const docsData = getDocViewData(file as AttachesItem, true);
                        iframeDocsRef?.current?.contentWindow?.postMessage(
                            {
                                type: 'docsData',
                                docsData,
                            },
                            `${window.location.origin}${ATTACHES_DOCS_URL}`
                        );
                    }

                    return;
                }

                const data = String(event.data);
                const message = data.split(' ')[0];

                switch (message) {
                    case 'app.started':
                    case 'loaded':
                        break;

                    case 'rendered': {
                        if (errorTimeout.current) {
                            clearTimeout(errorTimeout.current);
                        }

                        if (viewerApi.isDocsMail) {
                            setDocumentLoaded();
                        }

                        xray.send('view-doc-pm-rndrd', {
                            i: viewerApi.type,
                        });
                        break;
                    }

                    case 'ms-success':
                        onReady({ skipSendAnalytics: false });
                        break;

                    case 'fail':
                    case 'status':
                        clearTimeout(errorTimeout.current);
                        setDocState({
                            isError: true,
                            isLoaded: false,
                        });
                        onReady({ skipSendAnalytics: true });
                        xray.send(`view-doc-pm-err_${message}`, {
                            i: viewerApi.type,
                            rlog,
                            rlog_message: {
                                error: 'post message err',
                                viewer: viewerApi.type,
                                data,
                            },
                        });
                        break;
                }
            },
            [errorTimeout, isAttach, viewerApi]
        );

        const getDocumentViewUrl = useCallback(async (): Promise<[string, Record<string, string>]> => {
            let item: CloudItem | { id: string; isStock: boolean } = file;
            if ('attachType' in item && item.attachType === EAttachTypes.cloudStock) {
                item = {
                    id: getStockIdOfFile(item.id, file.name),
                    isStock: true,
                };
            }

            if (isAttach && viewerApi.isMsViewer) {
                return Promise.resolve([url, {}]);
            }

            if (viewerApi.isDocsMail) {
                if (url) {
                    return Promise.resolve([url, {}]);
                }

                if (!item) {
                    return Promise.reject();
                }

                const editorUrl = 'editors' in item ? item.editors?.docs?.url : null;
                const res = await getDocsView(editorUrl);
                return [res?.body?.url, {}];
            }

            if (!viewerApi.getViewUrl) {
                return Promise.reject();
            }

            let wopiData;

            if (isWopiEnabled && viewerApi.wopi) {
                try {
                    if (isMyOfficeForMailWopi) {
                        const { url, post_params, post_content_type } = docViewData;

                        if (url && post_params && post_content_type) {
                            xray.send('got-all-wopi-fields');
                        } else {
                            xray.send('got-wopi-fields-error', {
                                rlog: 'got-wopi-fields-error',
                                rlog_message: {
                                    url,
                                    post_params,
                                    post_content_type,
                                },
                            });
                        }

                        wopiData = {
                            url,
                            access_token: post_params?.access_token,
                            access_token_ttl: post_params?.access_token_ttl,
                            post_content_type,
                        };
                    }

                    // cloud-stock аттачи не показываем в просмотрщике, поэтому не надо ходить в вопи
                    if (!isAttach && !isCloudStock && !isEmbedded) {
                        const { data, status } = await callWopiViewAPICall(item.id, viewerApi.type);
                        wopiData = {
                            ...data,
                            status,
                        };
                    }

                    if (isEmbedded) {
                        const { name, url } = file || {};
                        const { data, status } = await callWopiEmbeddedViewAPICall(
                            {
                                name,
                                email: 'anonym',
                                url: url?.view || '',
                            },
                            viewerApi.type
                        );

                        wopiData = { ...data, status };
                    }
                } catch (error) {
                    if (isMyOfficeForMailWopi) {
                        xray.send('attach-wopi-data-error', {
                            rlog: 'attach-wopi-data-error',
                            rlog_message: {
                                error,
                            },
                        });
                    }

                    logger.error(error);
                    captureException(error);
                    return Promise.reject();
                }
            }

            const viewUrl = await viewerApi.getViewUrl(item, wopiData);

            return [viewUrl, wopiData];
        }, [file, isAttach, isEmbedded, viewerApi, url, docViewData]);

        const getMyOfficeIframe = useCallback(
            (myOfficeVariant: MYOFFICE_VARIANTS) => {
                const id = getMyOfficeIframeId(docId, myOfficeVariant);
                const ref = myOfficeVariant === MYOFFICE_VARIANTS.wopi ? iframeWopiRef : iframeAmrRef;

                return (
                    <iframe
                        id={id}
                        name={id}
                        src={url}
                        allowFullScreen
                        frameBorder={0}
                        className={styles.doc}
                        onError={handleError}
                        ref={ref}
                        {...(isWopiEnabled && { onLoad: setVisibilityListener })}
                    />
                );
            },
            [iframeAmrRef, iframeWopiRef]
        );

        // Урл документа загрузился - отображаем в просмотрщике
        useEffect(() => {
            let onLoadIframe: undefined | (() => Promise<void>) | (() => void);

            if (!viewerApi.type) {
                sendXray('try-show-notype');
                handleError();
                return;
            }

            // Для wopi варианта моего офиса нужно ходить по особой схеме
            // Иначе это может привести к попытке открыть MS
            if (attachDocUrl && !isMyOfficeForMailWopiFeature) {
                getDocumentUrl({ url: attachDocUrl })
                    .then((url) => {
                        // todo: заглушка, пока не поддержали мой офис в архивах
                        if (viewerApi.isMyOffice) {
                            sendXray('try-show-arch-myof');
                            handleError();
                            return;
                        }

                        setUrl(url);
                        setDocState({
                            isError: false,
                            isLoaded: true,
                        });
                    })
                    .catch(handleError);
            } else {
                if (isMyOfficeForMailWopi && docViewData.isDocumentsViewWopiLoading) {
                    // ждем пока подгрузится инфа для wopi
                    return;
                }
                if (isMyOfficeForMailWopi && !docViewData.isDocumentsViewWopiLoading && !docViewData.url) {
                    xray.send('try-load-wopi');
                    handleError();
                    return;
                }

                getDocumentViewUrl()
                    .then(([viewUrl, wopiData]) => {
                        if (viewUrl.length > 0) {
                            if (viewerApi.isMyOffice) {
                                const isCloudStock = (file as AttachesItem).attachType === EAttachTypes.cloudStock;
                                const isTemporary = (file as AttachesItem).attachType === EAttachTypes.temporary;

                                if (isCloudStock || (isTemporary && !isTemporaryAttachView)) {
                                    xray.send('try-show-temp-myof');
                                    handleError();
                                    return;
                                }

                                if ((IS_PUBLIC || IS_STOCK) && !isMyOfficeForPublicAndStock) {
                                    xray.send('try-show-cloud-myof');
                                    handleError();
                                    return;
                                }

                                setShowMyOfficeProgressBar(true);

                                if (!docState.isLoaded) {
                                    const wopiParams = {
                                        accessParams: {
                                            access_token: wopiData?.access_token,
                                            access_token_ttl: wopiData?.access_token_ttl,
                                        },
                                        postContentType: wopiData?.post_content_type,
                                        url: viewUrl,
                                    };

                                    const amrParams = {
                                        item: file as EditorItem,
                                        initShowAmrIframe,
                                    };

                                    const listeners = {
                                        onReady: onDocumentReady,
                                        onError: handleError,
                                    };

                                    if (amrParams.item.ext === 'csv') {
                                        setShowMyOfficeProgressBar(false);
                                    }

                                    onLoadIframe = initAndLoadMyOffice({
                                        iframeAmr: iframeAmrRef.current,
                                        iframeWopi: iframeWopiRef.current,
                                        isMyOfficeForMail: isMyOfficeForMail && isAttach,
                                        isEmbedded,
                                        wopiParams,
                                        amrParams,
                                        myOfficeVariant,
                                        listeners,
                                        item: file as EditorItem,
                                    });
                                }
                            } else if (viewerApi.isR7) {
                                initAndLoadR7(viewUrl, {
                                    placeholderId: iframeRef.current?.id ?? docId,
                                    container: document.body,
                                    onDocumentReady,
                                    onError() {},
                                });
                            } else if (viewerApi.isDocsMail) {
                                setUrl(viewUrl);
                            } else {
                                setUrl(viewUrl);
                                setDocumentLoaded();
                            }

                            xray.send('view-doc-loaded');
                        }
                    })
                    .catch(handleError);
            }

            return () => {
                if (onLoadIframe !== undefined) {
                    iframeRef?.current?.removeEventListener('load', onLoadIframe as EventListenerOrEventListenerObject);
                }
            };
        }, [
            file,
            handleError,
            onDocumentReady,
            viewerApi,
            isAttach,
            isEmbedded,
            isMyOfficeForMail,
            attachDocUrl,
            getDocumentViewUrl,
            docViewData,
            activeIframe,
        ]);

        useEffect(() => {
            if (isMyOfficeForMailWopi) {
                xray.send('myoffice-attach-switch-on');
            }

            if (errorTimeout.current) {
                clearTimeout(errorTimeout.current);
            }

            errorTimeout.current = window.setTimeout(() => {
                xray.send('view-doc-timeout', {
                    i: viewerApi.type,
                    rlog,
                    rlog_message: {
                        error: 'timeout',
                        viewer: viewerApi.type,
                    },
                });
                handleError();
            }, CONTENT_LOAD_TIMEOUT);

            window.addEventListener('message', handlePostMessage);

            return () => {
                window.removeEventListener('message', handlePostMessage);
                if (errorTimeout.current) {
                    clearTimeout(errorTimeout.current);
                }
            };
        }, [url, handleError, errorTimeout]);

        const { isError, isLoaded } = docState;

        if (isError) {
            return renderError();
        }

        const isMyOfficeProgressCondition = viewerApi.isMyOffice && isMyOfficeProgressBar && showMyOfficeProgressBar && !isWopiEnabled; // Для серверного моего офиса не нужен прогресс бар

        return (
            <div className={classNames(styles.root, { [styles.root_embedded]: embedded, [styles.root_mobile]: isMobile })}>
                {isMyOfficeProgressCondition && <ProgressBar docLoaded={isLoaded} error={false} closeFunc={showMyOfficeIframe} />}

                {(Boolean(url) ||
                    /* R7 и myOffice рендерит сам в DOC_PLACEHOLDER_ID  */
                    viewerApi.isR7) && (
                    <div
                        className={classNames(styles.iframeWrapper, {
                            [styles.iframeWrapper_r7]: viewerApi.isR7,
                            [styles.iframeWrapper_hidden]: viewerApi.isDocsMail && !isLoaded && !isError,
                        })}
                    >
                        <iframe
                            id={docId}
                            name={docId}
                            src={url}
                            allowFullScreen
                            frameBorder={0}
                            className={classNames(styles.doc, {
                                [styles.docMobile]: isMobile,
                                [styles.iframeDocsMail]: viewerApi.isDocsMail,
                            })}
                            onError={handleError}
                            ref={iframeRef}
                        />
                    </div>
                )}

                {viewerApi.isMyOffice && activeIframe && (
                    <div className={classNames(styles.doc, { [styles.docMobile]: isMobile })}>
                        {myOfficeVariant === MYOFFICE_VARIANTS.wopi && getMyOfficeIframe(MYOFFICE_VARIANTS.wopi)}
                        {getMyOfficeIframe(MYOFFICE_VARIANTS.amr)}
                    </div>
                )}

                {!url && !isLoaded && (
                    <div>
                        {isAttach && viewerApi.isMsViewer && <iframe frameBorder={0} src={ATTACHES_DOCS_URL} />}
                        {!isMyOfficeProgressCondition && !activeIframe && <Spinner />}
                    </div>
                )}

                {isMyOfficeForMailWopi && docViewData.isDocumentsViewWopiLoading && viewerApi.wopi && (
                    <iframe ref={iframeDocsRef} frameBorder={0} src={ATTACHES_DOCS_URL} />
                )}
            </div>
        );
    },
    IS_MOBILE_BROWSER ? shallowEqualMobile : shallowEqual
);

ViewerDoc.displayName = 'ViewerDoc';

ViewerDoc.whyDidYouRender = true;
